import { useCallback, useState } from 'react';
import { Button, Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { faLink } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import optainCameraImg from 'src/assets/img/optain-camera.png';

import useLocalStore from 'src/store/useLocalStore';
import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import { Api } from 'src/models/Api.model';
import CameraConnection from 'src/models/Camera/CameraConnection';
import { I18NextNS } from 'src/models/Language.model';

import { captureError } from 'src/utils/errorReporting';

export type ConnectCameraListProps = {
  ipList: string[];
};

export default function ConnectCameraList({ ipList }: ConnectCameraListProps) {
  const { setCameraData } = useRemoteBoundStore();
  const { setCameraIP } = useLocalStore();
  const [connectingToIP, setConnectingToIP] = useState<string>('');

  const {
    t,
    i18n: { language },
  } = useTranslation(I18NextNS.PAGES);

  const handleConnectCamera = useCallback(
    async (ip: string) => {
      try {
        setConnectingToIP(ip);
        const cameraData = await CameraConnection.loadCameraData(
          ip,
          t('message.error.cameraIpMissingMsg', { ns: I18NextNS.COMMON }),
        );
        await CameraConnection.getInstance().say(
          'Camera successfully connected.',
        );

        setCameraData(cameraData);
        setCameraIP(ip);
      } catch (error) {
        captureError(error, 'Failed to connect to camera');

        Api.alertBox({
          title: t('bootbox.title.Error', { ns: I18NextNS.COMMON }),
          message: t('message.error.failedToConnectToCamera', {
            ns: I18NextNS.COMMON,
          }),
          locale: language,
          type: 'error',
        });
      } finally {
        setConnectingToIP('');
      }
    },
    [language, setCameraData, setCameraIP, t],
  );

  return (
    <div className="d-flex flex-wrap gap-4">
      {ipList.map((ip) => (
        <Card
          key={ip}
          border="dark"
          className="p-3 mb-3"
          style={{ borderRadius: 20, borderWidth: 2, width: '23rem' }}
        >
          <div className="d-flex justify-content-between px-2">
            <div className="d-flex flex-column gap-2">
              <div>
                <h5>FC162</h5>
                <h5>({ip})</h5>
              </div>
              <Button
                variant="primary"
                onClick={() => {
                  handleConnectCamera(ip);
                }}
                disabled={connectingToIP === ip}
              >
                <FontAwesomeIcon icon={faLink} className="me-2" />
                {connectingToIP === ip
                  ? `${t('settings.cameraModes.cameraConnection.connecting')}...`
                  : t('settings.cameraModes.cameraConnection.connect')}
              </Button>
            </div>
            <div>
              <img
                src={optainCameraImg}
                style={{ width: 120 }}
                alt={t('settings.tabs.Camera')}
              />
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}
